import { __assign, __read, __rest } from "tslib";
/* eslint-disable react/no-this-in-sfc */
/**
 * @see https://github.com/NervJS/taro/blob/next/packages/taro-components/src/components/swiper/swiper.tsx
 */
import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import SwiperJS from "swiper";
import { uuid } from "../../../_util/uuid";
import { noop } from "../../../_util/noop";
import { useDefault } from "../../../_util/use-default";
import { useIsReady } from "../../../_util/use-is-ready";
import { mergeRefs } from "../../../_util/merge-refs";
export var Swiper = React.forwardRef(function Swiper(_a, ref) {
    var vertical = _a.vertical, indicatorDots = _a.indicatorDots, _b = _a.indicatorColor, indicatorColor = _b === void 0 ? "rgba(0, 0, 0, .3)" : _b, _c = _a.indicatorActiveColor, indicatorActiveColor = _c === void 0 ? "#000000" : _c, _d = _a.previousMargin, previousMargin = _d === void 0 ? 0 : _d, _e = _a.nextMargin, nextMargin = _e === void 0 ? 0 : _e, circular = _a.circular, _f = _a.displayMultipleItems, displayMultipleItems = _f === void 0 ? 1 : _f, autoplay = _a.autoplay, _g = _a.interval, interval = _g === void 0 ? 5000 : _g, _h = _a.duration, duration = _h === void 0 ? 500 : _h, _j = _a.spaceBetween, spaceBetween = _j === void 0 ? 0 : _j, _k = _a.zoom, zoom = _k === void 0 ? false : _k, _l = _a.animationOnSetCurrent, animationOnSetCurrent = _l === void 0 ? false : _l, className = _a.className, children = _a.children, _m = _a.style, style = _m === void 0 ? {} : _m, _o = _a.onChange, onChange = _o === void 0 ? noop : _o, _p = _a.onAnimationFinish, onAnimationFinish = _p === void 0 ? noop : _p, _q = _a.onSwiperClick, onSwiperClick = _q === void 0 ? noop : _q, onClick = _a.onClick, // 留空外层控制
    outerRef = _a.swiperRef, props = __rest(_a, ["vertical", "indicatorDots", "indicatorColor", "indicatorActiveColor", "previousMargin", "nextMargin", "circular", "displayMultipleItems", "autoplay", "interval", "duration", "spaceBetween", "zoom", "animationOnSetCurrent", "className", "children", "style", "onChange", "onAnimationFinish", "onSwiperClick", "onClick", "swiperRef"]);
    var idRef = useRef(uuid());
    var elementRef = useRef(null);
    var swiperRef = useRef(null);
    var ready = useIsReady();
    var currentRef = useRef(props.current);
    var _r = __read(useDefault(props.current, 0, function (current) {
        var event = {
            detail: {
                current: current,
                source: "",
            },
        };
        onChange(event);
    }), 2), current = _r[0], onCurrentChange = _r[1];
    // current
    useEffect(function () {
        currentRef.current = current;
        if (!ready || !swiperRef.current) {
            return;
        }
        var n = Number(current);
        if (Number.isNaN(n)) {
            return;
        }
        if (circular) {
            if (!swiperRef.current.isBeginning && !swiperRef.current.isEnd) {
                swiperRef.current.slideToLoop(n, animationOnSetCurrent ? undefined : 0); // 更新下标
            }
        }
        else {
            swiperRef.current.slideTo(n, animationOnSetCurrent ? undefined : 0); // 更新下标
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current]);
    // circular
    useEffect(function () {
        if (!ready || !swiperRef.current) {
            return;
        }
        swiperRef.current.params.loop = circular;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [circular]);
    // circular
    useEffect(function () {
        if (!ready || !swiperRef.current) {
            return;
        }
        swiperRef.current.params.loop = circular;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [circular]);
    // interval
    useEffect(function () {
        if (!ready || !swiperRef.current) {
            return;
        }
        if (swiperRef.current.autoplay && autoplay) {
            swiperRef.current.params.autoplay = {
                delay: parseInt(String(interval), 10),
                disableOnInteraction: false,
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interval]);
    useEffect(function () {
        if (swiperRef.current) {
            swiperRef.current.update();
        }
    });
    useEffect(function () {
        var visited = [];
        var options = {
            pagination: {
                el: ".tea-core-swiper-" + idRef.current + " > .swiper-container > .swiper-pagination",
            },
            direction: vertical ? "vertical" : "horizontal",
            loop: circular,
            slidesPerView: displayMultipleItems,
            initialSlide: current,
            speed: duration,
            zoom: zoom,
            observer: true,
            observeParents: true,
            spaceBetween: spaceBetween,
            on: {
                slideChange: function () {
                    if (currentRef.current !== this.realIndex) {
                        onCurrentChange(this.realIndex);
                    }
                },
                slideChangeTransitionStart: function () {
                    var $wrapperEl = this.$wrapperEl;
                    var params = this.params;
                    if (!visited[this.realIndex]) {
                        visited[this.realIndex] = true;
                        $wrapperEl
                            .children("." + params.slideClass + "." + params.slideDuplicateClass)
                            .each(function () {
                            var idx = this.getAttribute("data-swiper-slide-index");
                            this.innerHTML = $wrapperEl
                                .children("." + params.slideClass + "[data-swiper-slide-index=\"" + idx + "\"]:not(." + params.slideDuplicateClass + ")")
                                .html();
                        });
                    }
                },
                slideChangeTransitionEnd: function () {
                    this.slideToLoop(this.realIndex, 0, false);
                },
                transitionEnd: function () {
                    var event = {
                        detail: {
                            current: this.realIndex,
                            source: "",
                        },
                    };
                    onAnimationFinish(event);
                },
                observerUpdate: function (e) {
                    if (e.target && e.target.className === "swiper-wrapper") {
                        if (e.addedNodes.length > 0 || e.removedNodes.length > 0) {
                            this.loopDestroy();
                            this.loopCreate();
                        }
                    }
                },
                click: function (event) {
                    onSwiperClick(this.clickedIndex, { event: event });
                },
            },
        };
        if (autoplay) {
            options.autoplay = {
                delay: parseInt(String(interval), 10),
                disableOnInteraction: false,
            };
        }
        swiperRef.current = new SwiperJS(".tea-core-swiper-" + idRef.current + " > .swiper-container", options);
        if (outerRef) {
            mergeRefs(outerRef)(swiperRef.current);
        }
        return function () {
            swiperRef.current.destroy(true, true);
            swiperRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vertical, circular, autoplay, spaceBetween, zoom]);
    return (React.createElement("div", { key: vertical + "-" + circular + "-" + autoplay, ref: mergeRefs(ref, elementRef), className: classNames("tea-core-swiper", "tea-core-swiper-" + idRef.current, className), style: __assign(__assign(__assign({}, style), (vertical
            ? {
                paddingTop: previousMargin,
                paddingBottom: nextMargin,
            }
            : {
                paddingLeft: previousMargin,
                paddingRight: nextMargin,
            })), { overflow: "hidden" }) },
        React.createElement("div", { className: "swiper-container", style: { overflow: "visible" } },
            React.createElement("style", { type: "text/css" }, "\n              .tea-core-swiper-" + idRef.current + " > .swiper-container > .swiper-pagination > .swiper-pagination-bullet { background: " + indicatorColor + " }\n              .tea-core-swiper-" + idRef.current + " > .swiper-container > .swiper-pagination > .swiper-pagination-bullet-active { background: " + indicatorActiveColor + " }\n            "),
            React.createElement("div", { className: "swiper-wrapper" }, children),
            React.createElement("div", { className: classNames("swiper-pagination", {
                    "swiper-pagination-hidden": !indicatorDots,
                    "swiper-pagination-bullets": indicatorDots,
                }) }))));
});
//# sourceMappingURL=Swiper.js.map